import axios from 'axios';

// Normalize and sanitize a base URL string
const sanitizeBaseUrl = (value) => {
  try {
    if (!value || typeof value !== 'string') return '';
    let s = value.trim();
    // Fix accidental spaces after protocol like 'http: //'
    s = s.replace(/^http:\s*\/\//i, 'http://').replace(/^https:\s*\/\//i, 'https://');
    // If it starts with '//' add http
    if (s.startsWith('//')) s = 'http:' + s;
    // If no protocol, assume http
    if (!/^https?:\/\//i.test(s)) s = 'http://' + s;
    // Validate via URL constructor
    const u = new URL(s);
    // Preserve any base path (e.g. '/api' for a reverse-proxied backend),
    // dropping only the trailing slash. No path → returns just the origin.
    const basePath = u.pathname.replace(/\/+$/, '');
    return u.origin + basePath;
  } catch {
    return '';
  }
};

const envBaseRaw = (process.env.REACT_APP_API_BASE_URL || '').toString();
let envBase = sanitizeBaseUrl(envBaseRaw);
let derivedBase = '';
if (typeof window !== 'undefined' && window.location?.origin) {
  try {
    const loc = new URL(window.location.origin);
    const port = loc.port === '3000' ? '8000' : (loc.port || '8000');
    derivedBase = `${loc.protocol}//${loc.hostname}:${port}`;
  } catch {
    derivedBase = 'http://localhost:8000';
  }
}
if (!derivedBase) derivedBase = 'http://localhost:8000';

export const API_BASE_URL = envBase || derivedBase;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = String(error.config?.url || '');
    const isLoginRequest = requestUrl.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const loginPath = `${process.env.PUBLIC_URL || ''}/login`;
      if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  heartbeat: () => api.post('/auth/heartbeat'),
};

// Personality API
export const personalityAPI = {
  getQuestions: () => api.get('/personality/questions'),
  submitAnswers: (payload) => api.post('/personality/submit', payload),
};

// Consent API
export const consentAPI = {
  getStatus: () => api.get('/consent'),
  giveConsent: () => api.post('/consent', { consent: true }),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (settings) => api.post('/admin/settings', { settings }),
};

// Messages API
export const messagesAPI = {
  getMessages: () => api.get('/messages'),
  sendMessage: (content) => api.post('/messages', { content }),
};

// Admin Tasks API
export const adminTasksAPI = {
  list: () => api.get('/admin/tasks'),
  create: (task) => api.post('/admin/tasks', task),
  update: (id, task) => api.put(`/admin/tasks/${id}`, task),
  remove: (id) => api.delete(`/admin/tasks/${id}`),
  setMain: (taskId) => api.post('/admin/tasks/main', { task_id: taskId }),
  unsetMain: () => api.delete('/admin/tasks/main'),
};

// Chat API
export const chatAPI = {
  getRoomTask: (roomId) => api.get(`/chat/room/${roomId}/task`),
  getRoomTimer: (roomId) => api.get(`/chat/room/${roomId}/timer`),
};

// WebSocket connection
export const createWebSocketConnection = (userId) => {
  return new WebSocket(`${WS_BASE_URL}/ws/${userId}`);
};

export default api;

// WebSocket base URL matching API host
export const WS_BASE_URL = (() => {
  try {
    const u = new URL(API_BASE_URL);
    const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${u.host}`;
  } catch {
    // Fallback to window location
    if (typeof window !== 'undefined') {
      const loc = window.location;
      const wsProtocol = loc.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${loc.host.replace(':3000', ':8000')}`;
    }
    return 'ws://localhost:8000';
  }
})();
